/**
 * useReopenOnComment Hook
 *
 * Gates a comment submit behind a re-open prompt when the task is already closed.
 * Tasks often get new work after they're marked Completed, and assignees never
 * notice because the status stays closed — so commenting on one asks whether to
 * re-open it first.
 *
 * @example
 * const { reopenPromptVisible, submitWithReopenCheck, ...handlers } =
 *   useReopenOnComment({ currentTask });
 * <Form onFinish={(values) => submitWithReopenCheck(() => handleSubmitComment(values))} />
 */

import { useCallback, useRef, useState } from 'react';
import { message } from 'antd';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { CLOSED_STATUSES, getStatusLabel, statusTypes } from '../configs/task.config';

// Status a re-opened task lands in.
const REOPEN_TARGET_STATUS = statusTypes['In Progress'];

/**
 * @param {Object} options
 * @param {Object} options.currentTask - Task the comment is being posted on
 * @param {Function} [options.onReopened] - Called after the status flip succeeds
 * @returns {Object} Prompt state and handlers
 */
export function useReopenOnComment({ currentTask, onReopened }) {
  const { updateTask, refetchTask, fetchHistories } = useTaskContext();

  const [reopenPromptVisible, setReopenPromptVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // The submit deferred while the prompt is open, plus the task it belongs to: the drawer/page
  // can be pointed at another task before the user answers, and the re-open must follow the
  // comment, not whatever task is on screen by then.
  const pendingSubmitRef = useRef(null);

  const taskId = currentTask && currentTask.id;
  const status = currentTask && currentTask.status;
  const isClosed = CLOSED_STATUSES.includes(status);

  /**
   * Run `submit` now, or defer it behind the re-open prompt on a closed task.
   * @param {Function} submit - Resolves truthy when the comment posted
   */
  const submitWithReopenCheck = useCallback(
    (submit) => {
      if (!isClosed) {
        return submit();
      }
      pendingSubmitRef.current = { submit, taskId };
      setReopenPromptVisible(true);
      return undefined;
    },
    [isClosed, taskId]
  );

  // Confirming posts AND re-opens — there is no post-without-re-opening path. Stays open while
  // the comment posts (attachments can be slow) so the prompt's buttons act as the busy state
  // instead of the page accepting another submit behind a closed modal.
  const confirmReopen = useCallback(async () => {
    const pending = pendingSubmitRef.current;
    // Cleared up front, not in the finally: a second click must not start a second post.
    pendingSubmitRef.current = null;
    if (!pending) {
      setReopenPromptVisible(false);
      return;
    }

    try {
      setSubmitting(true);
      // Post first: a failed comment must not leave the task re-opened for nothing.
      const posted = await pending.submit();
      if (!posted) return;

      await updateTask({
        variables: { data: { status: REOPEN_TARGET_STATUS }, where: { id: pending.taskId } },
      });
      if (refetchTask) await refetchTask();
      // The status dropdowns refresh the activity log after a status change; do the same here
      // so a re-open shows up in History without switching tabs.
      if (fetchHistories) {
        await fetchHistories({
          variables: { where: { referenceId: pending.taskId, type: 'TASK' } },
        });
      }
      if (onReopened) onReopened();
      message.success(`Task re-opened and set to ${getStatusLabel(REOPEN_TARGET_STATUS)}.`);
    } catch (error) {
      message.error(`Comment posted, but the task could not be re-opened: ${error.message}`);
    } finally {
      setSubmitting(false);
      setReopenPromptVisible(false);
    }
  }, [updateTask, refetchTask, fetchHistories, onReopened]);

  const cancelReopenPrompt = useCallback(() => {
    if (submitting) return;
    pendingSubmitRef.current = null;
    setReopenPromptVisible(false);
  }, [submitting]);

  return {
    reopenPromptVisible,
    submitting,
    currentStatus: status,
    reopenStatus: REOPEN_TARGET_STATUS,
    submitWithReopenCheck,
    confirmReopen,
    cancelReopenPrompt,
  };
}

export default useReopenOnComment;
